import React, { useState, useCallback, useRef, useEffect } from 'react';
import { OrgNode, DepartmentConfig } from './OrgChart';
import { EmployeeCard } from './EmployeeCard';
import { getDeptConfig, isUzefDurrani, isActualDeptAdmin, isActualLead, normalizeDept } from './OrgChart';
import { EmptyState } from '@/shared/components/ui/EmptyState';

interface HierarchyCanvasProps {
  roots: OrgNode[];
  search: string;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  isAdmin: boolean;
  onReassign: (node: OrgNode) => void;
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onCenter: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  centerTrigger?: number;
}

function nodeMatchesSearch(node: OrgNode, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    node.display_name.toLowerCase().includes(q) ||
    (node.email || '').toLowerCase().includes(q) ||
    (node.department || '').toLowerCase().includes(q) ||
    (node.position || '').toLowerCase().includes(q) ||
    (node.role || '').toLowerCase().includes(q)
  );
}

function treeContainsMatch(node: OrgNode, query: string): boolean {
  if (nodeMatchesSearch(node, query)) return true;
  return node.children.some((child) => treeContainsMatch(child, query));
}

function getNodeTier(node: OrgNode): 'executive' | 'admin' | 'lead' | 'employee' {
  if (isUzefDurrani(node)) return 'executive';
  if (isActualDeptAdmin(node)) return 'admin';
  if (isActualLead(node)) return 'lead';
  return 'employee';
}

function filterVisibleChildren(node: OrgNode, search: string, collapsed: Set<string>): OrgNode[] {
  if (collapsed.has(node.id)) return [];
  return node.children.filter((c) => treeContainsMatch(c, search));
}

export function HierarchyCanvas({
  roots,
  search,
  collapsed,
  onToggle,
  isAdmin,
  onReassign,
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onCenter,
  onExpandAll,
  onCollapseAll,
  centerTrigger = 0,
}: HierarchyCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number }>({
    x: 0,
    y: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  // Center tree horizontally in canvas viewport
  const centerCanvas = useCallback((smooth = true) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const targetScrollLeft = (canvas.scrollWidth - canvas.clientWidth) / 2;
    if (targetScrollLeft > 0) {
      canvas.scrollTo({
        left: targetScrollLeft,
        top: 0,
        behavior: smooth ? 'smooth' : 'auto',
      });
    } else {
      canvas.scrollTo({ left: 0, top: 0, behavior: smooth ? 'smooth' : 'auto' });
    }
  }, []);

  // Sync with centerTrigger from toolbar or onCenter
  useEffect(() => {
    if (centerTrigger > 0) {
      centerCanvas(true);
    }
  }, [centerTrigger, centerCanvas]);

  // Center on initial mount once roots are ready
  useEffect(() => {
    if (roots.length > 0) {
      const timer = setTimeout(() => {
        centerCanvas(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [roots.length, centerCanvas]);

  // Handle mouse wheel zoom when Ctrl is pressed, normal scroll otherwise
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (e.deltaY < 0) onZoomIn();
        else onZoomOut();
      }
    },
    [onZoomIn, onZoomOut]
  );

  // Handle smooth drag-to-scroll panning on canvas background
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Don't drag if user clicked an interactive control or employee card
    if (target.closest('.employee-card, button, a, input, select, textarea, [data-interactive="true"]')) {
      return;
    }

    if (e.button === 0 || e.button === 1) { // Left or middle click
      setIsPanning(true);
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: canvasRef.current?.scrollLeft || 0,
        scrollTop: canvasRef.current?.scrollTop || 0,
      };
    }
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPanning || !canvasRef.current) return;
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      canvasRef.current.scrollLeft = panStartRef.current.scrollLeft - dx;
      canvasRef.current.scrollTop = panStartRef.current.scrollTop - dy;
    },
    [isPanning]
  );

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      switch (e.key) {
        case '=':
        case '+':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            onZoomIn();
          }
          break;
        case '-':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            onZoomOut();
          }
          break;
        case '0':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            onZoomReset();
            centerCanvas(true);
          }
          break;
        case 'c':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            centerCanvas(true);
          }
          break;
        case 'e':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            onExpandAll();
          }
          break;
        case 'h':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            onCollapseAll();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onZoomIn, onZoomOut, onZoomReset, centerCanvas, onExpandAll, onCollapseAll]);

  if (roots.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <EmptyState
          title="No team members found"
          description="Try selecting 'All' or a different department, or clear your search."
        />
      </div>
    );
  }

  return (
    <div
      ref={canvasRef}
      className="hierarchy-canvas-container relative flex-1 min-h-0 w-full h-full overflow-x-auto overflow-y-auto rounded-xl border border-[#E8E8F5] bg-white p-6 shadow-inner hierarchy-scrollbar"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        cursor: isPanning ? 'grabbing' : 'grab',
        userSelect: isPanning ? 'none' : 'auto',
      }}
    >
      <div
        className="flex min-w-full w-max min-h-full flex-col items-center justify-start pb-20 pt-4 transition-transform duration-150"
        style={{
          zoom: zoomLevel !== 100 ? `${zoomLevel}%` : undefined,
        }}
      >
        {roots.map((root, i) => (
          <div key={root.id} className={i > 0 ? 'mt-12' : ''}>
            <TreeNodeRenderer
              node={root}
              search={search}
              collapsed={collapsed}
              onToggle={onToggle}
              isAdmin={isAdmin}
              onReassign={onReassign}
              isRoot={true}
              level={0}
            />
          </div>
        ))}
      </div>

      {/* Floating Status & Navigation Controls */}
      <div className="sticky bottom-2 left-2 z-10 inline-flex items-center gap-3 rounded-lg bg-white/95 backdrop-blur-sm border border-[#E8E8F5] px-3.5 py-1.5 shadow-md">
        <span className="text-xs font-medium text-[#747C9C]">
          Zoom: <strong className="text-[#11183F]">{zoomLevel}%</strong>
        </span>
        <span className="text-[#E8E8F5]">|</span>
        <button
          onClick={() => {
            onZoomReset();
            centerCanvas(true);
          }}
          className="text-xs font-semibold text-[#6825F5] hover:underline"
          title="Reset zoom to 100% (Ctrl+0)"
        >
          Reset
        </button>
        <span className="text-[#E8E8F5]">|</span>
        <button
          onClick={() => centerCanvas(true)}
          className="text-xs font-semibold text-[#6825F5] hover:underline"
          title="Center tree in view (Ctrl+C)"
        >
          Center View
        </button>
        <span className="hidden sm:inline text-[#E8E8F5]">|</span>
        <span className="hidden sm:inline text-[11px] text-[#8F95B2]">
          Drag background or use scrollbars to pan
        </span>
      </div>
    </div>
  );
}

function TreeNodeRenderer({
  node,
  search,
  collapsed,
  onToggle,
  isAdmin,
  onReassign,
  isRoot = false,
  level = 0,
}: {
  node: OrgNode;
  search: string;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  isAdmin: boolean;
  onReassign: (node: OrgNode) => void;
  isRoot?: boolean;
  level?: number;
}) {
  const hasChildren = node.children && node.children.length > 0;
  const isCollapsed = collapsed.has(node.id);
  const isMatch = search ? nodeMatchesSearch(node, search) : false;
  const subtreeMatch = search ? treeContainsMatch(node, search) : true;

  if (search && !subtreeMatch) return null;

  const visibleChildren = filterVisibleChildren(node, search, collapsed);
  const deptConfig = getDeptConfig(node.department);
  const tier = getNodeTier(node);

  return (
    <div className="flex flex-col items-center">
      <EmployeeCard
        node={node}
        tier={tier}
        hasChildren={hasChildren}
        isCollapsed={isCollapsed}
        isMatch={isMatch}
        deptConfig={deptConfig}
        onToggle={onToggle}
        onReassign={onReassign}
        isAdmin={isAdmin}
        isRoot={isRoot}
      />

      {/* Children Hierarchy Connector Lines */}
      {hasChildren && !isCollapsed && visibleChildren.length > 0 && (
        <div className="flex flex-col items-center">
          {/* Vertical trunk line from parent */}
          <div className="h-6 w-0.5 bg-[#E8E8F5]" />

          {visibleChildren.length === 1 ? (
            <TreeNodeRenderer
              node={visibleChildren[0]}
              search={search}
              collapsed={collapsed}
              onToggle={onToggle}
              isAdmin={isAdmin}
              onReassign={onReassign}
              level={level + 1}
            />
          ) : (
            <div className="relative flex items-start justify-center pt-3">
              {/* Horizontal branch bar linking all children */}
              <div
                className="absolute top-0 h-0.5 bg-[#E8E8F5]"
                style={{
                  left: 'calc(110px)',
                  right: 'calc(110px)',
                }}
              />

              {/* Children Nodes Grid */}
              <div className="flex gap-6">
                {visibleChildren.map((child) => (
                  <div key={child.id} className="relative flex flex-col items-center">
                    {/* Vertical drop line connecting branch bar to child */}
                    <div className="absolute -top-3 h-3 w-0.5 bg-[#E8E8F5]" />
                    <TreeNodeRenderer
                      node={child}
                      search={search}
                      collapsed={collapsed}
                      onToggle={onToggle}
                      isAdmin={isAdmin}
                      onReassign={onReassign}
                      level={level + 1}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}